import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Area,
  ReferenceLine,
  ReferenceDot,
  ReferenceArea,
  CartesianGrid,
} from "recharts";
import { getMoonPhase } from "../services/moonCalculation";
import { fetchWeatherApi } from "openmeteo";

/**
 * TideChart
 *
 * Props:
 *  - lat (number) : latitude
 *  - lon (number) : longitude
 *
 * Example:
 * <TideChart lat={2.195} lon={102.248} />
 * 2.186486, 102.242632
 */
export default function TideChart({ lat = 2.186486, lon = 102.242632 }) {
  const [data, setData] = useState(null); // array of { ts: number (ms), height: number }
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [sunTimes, setSunTimes] = useState(null); // { sunrise: ts(ms), sunset: ts(ms) }
  const [astro, setAstro] = useState({ cloud: 0, moonPct: 0, moonText: "Waxing", moonIcon: "🌕" });

  useEffect(() => {
    async function fetchAstro() {
      try {
        const url = `https://api.open-meteo.com/v1/forecast?latitude=2.186486&longitude=102.242632&current=cloud_cover&astronomy=moon_phase&astronomy_days=1&timezone=auto`;
        const res = await fetch(url);
        const data = await res.json();
        const { age, percent } = getMoonPhase();

        // 1. Get Cloud Cover (Simple %)
        const cloudCover = data.current.cloud_cover || 0;

        const phaseVal = age;

        let mIcon = "🌑";
        let mText = "New Moon";
        let mPct = 0;

        // Calculate simplified percentage (0 to 100% illumination)
        // Illumination is roughly distance from 0 (New) or 1 (New) to 0.5 (Full)
        // Math: 1 - (2 * |0.5 - phaseVal|)
        const illumination = (1 - (2 * Math.abs(0.5 - phaseVal))) * 100;
        mPct = Math.round(illumination);
        mPct = Math.round(percent);

        // Determine Text & Icon
        if (phaseVal === 0 || phaseVal < 1.845) { mText = "New Moon"; mIcon = "🌑"; }
        else if (phaseVal < 5.536) { mText = "Waxing Crescent"; mIcon = "🌒"; }
        else if (phaseVal < 9.228) { mText = "First Quarter"; mIcon = "🌓"; }
        else if (phaseVal < 12.919) { mText = "Waxing Gibbous"; mIcon = "🌔"; }
        else if (phaseVal < 16.610) { mText = "Full Moon"; mIcon = "🌕"; }
        else if (phaseVal < 20.302) { mText = "Waning Gibbous"; mIcon = "🌖"; }
        else if (phaseVal < 23.993) { mText = "Last Quarter"; mIcon = "🌗"; }
        else { mText = "Waning Crescent"; mIcon = "🌘"; }

        setAstro({
          cloud: cloudCover,
          moonPct: mPct,
          moonText: mText,
          moonIcon: mIcon
        });

      } catch (e) {
        console.error("Astro fetch failed", e);
      }
    }

    fetchAstro();
  }, [lat, lon]);
  // Fetch sunrise/sunset (to compute day shading). Using sunrise-sunset.org public API.
  useEffect(() => {
    // Format date based on local timezone to match tide data
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    console.log(dateString);
    const url = `https://api.sunrise-sunset.org/json?lat=${lat}&lng=${lon}&date=${dateString}&formatted=0`;
    fetch(url)
      .then((r) => r.json())
      .then((j) => {
        if (j.status === "OK" && j.results) {

          const sunriseUTC = new Date(j.results.sunrise).getTime();
          const sunsetUTC = new Date(j.results.sunset).getTime();

          setSunTimes({
            sunrise: sunriseUTC,
            sunset: sunsetUTC,
          });
        } else {
          const now = Date.now();
          setSunTimes({ sunrise: now - 4 * 3600 * 1000, sunset: now + 8 * 3600 * 1000 });
        }
      })
      .catch(() => {
        const now = Date.now();
        setSunTimes({ sunrise: now - 4 * 3600 * 1000, sunset: now + 8 * 3600 * 1000 });
      });
  }, [lat, lon]);

  useEffect(() => {
    setLoading(true);
    setErr(null);

    async function load() {
      try {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;

        const params = {
          latitude: lat,
          longitude: lon,
          hourly: "sea_level_height_msl",
          timezone: "Asia/Singapore",
          start_date: dateString,
          end_date: dateString,
        };
        const url = "https://marine-api.open-meteo.com/v1/marine";
        const responses = await fetchWeatherApi(url, params);

        const response = responses[0];
        // const utcOffsetSeconds = response.utcOffsetSeconds();
        const hourly = response.hourly();

        const weatherData = {
          time: Array.from(
            { length: (Number(hourly.timeEnd()) - Number(hourly.time())) / hourly.interval() },
            (_, i) => new Date((Number(hourly.time()) + i * hourly.interval()) * 1000)
          ),
          wave_height: hourly.variables(0).valuesArray(),
        };

        const chartData = weatherData.time.map((time, i) => ({
          ts: time.getTime(),
          height: weatherData.wave_height[i],
        }));

        setData(chartData);
        setLoading(false);

      } catch (e) {
        console.error(e);
        setErr(String(e));
        generateSampleData();
      }
    }

    function generateSampleData() {
      const sample = [];
      const start = new Date();
      start.setHours(0, 0, 0, 0); // Local midnight
      const startTs = start.getTime(); // Returns true UTC timestamp of local midnight

      for (let h = 0; h <= 24; h++) {
        const tIndex = h;
        const height = 1.3 * Math.sin((tIndex / 24) * Math.PI * 2) + 1.2;

        // Apply offset here so sample data matches the shifted API data
        const shiftedTs = startTs + (h * 3600 * 1000);

        sample.push({
          ts: shiftedTs,
          height: Math.round(height * 100) / 100
        });
      }
      setData(sample);
      setLoading(false);
    }

    load();
  }, [lat, lon]);


  // Derived: data formatted for Recharts (x: timestamp -> label)
  const rcData = useMemo(() => {
    if (!data) return [];
    return data.map((p) => ({
      x: p.ts,
      timeLabel: new Date(p.ts).toLocaleTimeString([], { hour: "numeric" }),
      height: p.height,
    }));
  }, [data]);

  // Determine min/max, highs and lows
  const { minH, maxH, highPoints, lowPoints, currentPoint } = useMemo(() => {
    if (!data || data.length === 0) return { minH: 0, maxH: 0, highPoints: [], lowPoints: [], currentPoint: null };
    const heights = data.map((d) => d.height);
    const minH_ = Math.min(...heights);
    const maxH_ = Math.max(...heights);

    const highs = [];
    const lows = [];
    for (let i = 1; i < data.length - 1; i++) {
      const prev = data[i - 1].height;
      const cur = data[i].height;
      const next = data[i + 1].height;
      if (cur >= prev && cur >= next) highs.push(data[i]);
      if (cur <= prev && cur <= next) lows.push(data[i]);
    }

    // current time point: nearest timestamp to now
    const now = Date.now();
    let nearest = data[0];
    let bestDiff = Math.abs(now - data[0].ts);
    for (const p of data) {
      const d = Math.abs(now - p.ts);
      if (d < bestDiff) {
        bestDiff = d;
        nearest = p;
      }
    }

    return { minH: minH_, maxH: maxH_, highPoints: highs, lowPoints: lows, currentPoint: nearest };
  }, [data]);

  const xDomain = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setHours(24, 0, 0, 0); // Next day midnight

    return [start.getTime(), end.getTime()];
  }, []);

  // Helper to format time nicely
  function formatTime(ts) {
    return new Date(ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }

  // Determine red popup label (value + up/down arrow) using nearest current and next point
  const redLabel = useMemo(() => {
    if (!currentPoint) return null;
    // find slope to next point (if exists)
    const idx = data.findIndex((d) => d.ts === currentPoint.ts);
    let arrow = "";
    if (idx >= 0 && idx + 1 < data.length) {
      const next = data[idx + 1].height;
      arrow = next < currentPoint.height ? "↓" : next > currentPoint.height ? "↑" : "";
    }
    return `${currentPoint.height.toFixed(2)} m ${arrow}`;
  }, [currentPoint, data]);

  // Customized SVG layer to draw red popup and moon/weather header

  const CustomTooltip = ({ active, payload, label }) => {

    if (active && payload && payload.length) {

      const data = payload[0].payload;

      const { height } = data;



      let arrow = "";

      const pointIndex = rcData.findIndex(d => d.x === label);

      if (pointIndex >= 0 && pointIndex + 1 < rcData.length) {

        const nextHeight = rcData[pointIndex + 1].height;

        arrow = nextHeight < height ? "↓" : nextHeight > height ? "↑" : "";

      }



      return (

        <div style={{ background: 'white', border: '1.5px solid red', padding: '5px 10px', borderRadius: '6px' }}>

          <p style={{ color: 'red', fontWeight: '700', margin: 0 }}>

            {`${height.toFixed(2)} m ${arrow}`}

          </p>

        </div>

      );

    }



    return null;

  };



  // Chart render

  return (

    <div style={{ width: "100%", fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, Arial" }}>

      <div style={{ background: "#e4e8edff", borderRadius: 5, padding: 1 }}>

        {/** Header area (top bar) is drawn inside customized overlay SVG too, but we keep here small info */}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>

          <div>

            <div style={{ fontSize: 14, fontWeight: 700, color: 'black' }}>{new Date().toLocaleDateString("en-GB", { day: 'numeric', month: 'short', year: 'numeric' }).toUpperCase()}</div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'black', marginTop: -2 }}>(Today)</div>

          </div>

          <div style={{ textAlign: "right", fontSize: 12, color: 'black' }}>

            {/* <div><span role="img" aria-label="moon">🌕</span> 99% Waxing</div>

              <div><span role="img" aria-label="cloud">☁️</span> 90%</div> */}
            {/* Moon Data */}
            <div title={astro.moonText}>
              <span role="img" aria-label="moon">{astro.moonIcon}</span> {astro.moonPct}% {astro.moonText.split(' ')[0]}
            </div>

            {/* Cloud Data */}
            <div>
              <span role="img" aria-label="cloud">☁️</span> {astro.cloud}%
            </div>

          </div>

        </div>



        <div style={{ height: 210, width: "100%" }}>

          <ResponsiveContainer width="100%" height="100%">

            <LineChart data={rcData} margin={{ left: 0, right: 10, top: 10, bottom: 0 }}>

              <CartesianGrid
                vertical={false}              // Hides vertical lines
                horizontal={true}             // Shows horizontal lines
                stroke="#ffffff"              // White lines
                strokeOpacity={0.2}           // 20% opacity (subtle)
              // strokeDasharray="3 3"        // Dashed pattern (optional)
              />

              {/* background gradient fill using Area and ReferenceArea for day/night */}

              {/* Day shading: use ReferenceArea between sunrise and sunset (if available) */}

              {sunTimes && (

                <ReferenceArea

                  x1={sunTimes.sunrise}

                  x2={sunTimes.sunset}

                  strokeOpacity={0}

                  fill="#eeeca6ff" // light green

                  fillOpacity={0.6}

                />

              )}



              {/* Night shading (left and right) — optional: we rely on chart background color too */}

              {sunTimes && <ReferenceLine x={sunTimes.sunrise} stroke="#FF7F50" strokeWidth={2} strokeDasharray="3 3" label={{ value: "Sunrise", position: "insideTopLeft", fill: "black", fontWeight: "bold" }} />}

              {sunTimes && <ReferenceLine x={sunTimes.sunset} stroke="#6A5ACD" strokeWidth={2} strokeDasharray="3 3" label={{ value: "Sunset", position: "insideBottomRight", fill: "black", fontWeight: "bold" }} />}



              {/* X axis: use timestamp values */}

              <XAxis

                dataKey="x"

                domain={xDomain}

                type="number"

                tickFormatter={(ts) => new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', hour12: true })}

                tick={{ fontSize: 10, fill: '#000000' }}
                ticks={useMemo(() => {
                  // Generate ticks: 12 AM, 6 AM, 12 PM, 6 PM, 12 AM
                  const [start, end] = xDomain;
                  const ticks = [];
                  // Loop every 6 hours
                  for (let t = start; t <= end; t += 6 * 3600 * 1000) {
                    ticks.push(t);
                  }
                  return ticks;
                }, [xDomain])}

                // ticks={useMemo(() => {

                //   if (!rcData || rcData.length === 0) return [];

                //   const [start] = xDomain;

                //   const startDate = new Date(start);

                //   startDate.setMinutes(0, 0, 0);

                //                       const ticks = [];

                //                       for (let i = 0; i < 25; i += 2) {

                //                           ticks.push(startDate.getTime() + i * 3600 * 1000);

                //                       }

                //                       return ticks;

                // }, [rcData, xDomain])}

                axisLine={true}

                tickLine={false}

              />



              <YAxis

                domain={[Math.floor(minH - 0.5), Math.ceil(maxH + 0.5)]}

                tickFormatter={(v) => `${v.toFixed(1)} m`}

                tick={{ fontSize: 10, fill: '#000000' }}

                axisLine={true}

                tickLine={false}

                width={56}

              />



              {/* filled area under the curve for blue water */}

              <Area

                type="monotone"

                dataKey="height"

                stroke="none"

                fill="url(#tidalGradient)"

                isAnimationActive={true}

                animationDuration={900}

              />

              {/* Define gradient */}

              <defs>

                <linearGradient id="tidalGradient" x1="0" x2="0" y1="0" y2="1">

                  <stop offset="0%" stopColor="#dfefff" stopOpacity="0.9" />

                  <stop offset="100%" stopColor="#eaf4ff" stopOpacity="0.2" />

                </linearGradient>

              </defs>



              {/* Tide line */}

              <Line

                type="monotone"

                dataKey="height"

                stroke="#184fc4"

                strokeWidth={3}

                dot={false}

                isAnimationActive={true}

                animationDuration={900}

              />



              {/* high tide / low tide dots */}

              {highPoints.map((p, idx) => (

                <ReferenceDot key={`h-${idx}`} x={p.ts} y={p.height} r={4} fill="#0b5cff" stroke="#fff" strokeWidth={1} />

              ))}

              {lowPoints.map((p, idx) => (

                <ReferenceDot key={`l-${idx}`} x={p.ts} y={p.height} r={3} fill="#0b5cff" stroke="#fff" strokeWidth={1} />

              ))}



              {/* Tooltip */}

              <Tooltip

                cursor={{ stroke: 'red', strokeWidth: 2 }}

                content={<CustomTooltip />}

                isAnimationActive={false}

                position={{ y: 50 }}

              />

            </LineChart>

          </ResponsiveContainer>

        </div>



        {/* footer: show nearest high & low */}

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5, color: "black" }}>

          <div>

            <strong>Low Tides:</strong>

            <div style={{ fontSize: 10, marginLeft: 4, color: "black" }}>

              {lowPoints.length ? `${lowPoints[0].height.toFixed(2)} m @ ${formatTime(lowPoints[0].ts)}` : "n/a"}

            </div>

          </div>



          <div style={{ textAlign: "right" }}>

            <strong>High Tides:</strong>

            <div style={{ fontSize: 10, marginLeft: 4, color: "black" }}>

              {highPoints.length ? `${highPoints[0].height.toFixed(2)} m @ ${formatTime(highPoints[0].ts)}` : "n/a"}

            </div>

          </div>

        </div>



        {loading && <div style={{ marginTop: 8, fontSize: 10, color: "black" }} >Loading tide data...</div>}

        {err && <div style={{ marginTop: 8, color: "crimson", fontSize: 13 }} >Error: {err}</div>}

      </div>

    </div>

  );

}



/**

 * CustomizedChartOverlay: a smaller, safer custom overlay separated from main component

 * Recharts gives a 'Customized' component but its props can be inconsistent — we'll draw

 * a separate lightweight overlay anchored to chart area by assuming margins similar to those used above.

 */

function CustomizedChartOverlay({ sunTimes, currentPoint, minH, maxH, xDomain }) {

  // This component returns null because inlined overlay (SVG) was implemented inside main via other techniques.

  // Keeping this hook for future extension. For now, render nothing.

  return null;

}

